import { motion, AnimatePresence } from "framer-motion";
import { useAssets, assetUrl } from "@/context/AssetsContext";
import {
  Car, KeyRound, Droplets, Star, MapPin, Send, Sparkles, Check,
  Navigation, Phone, ShieldCheck, Clock,
} from "lucide-react";

const StatusBar = ({ dark }) => (
  <div className={`flex items-center justify-between px-6 pt-3 pb-1 text-[11px] font-semibold ${dark ? "text-white" : "text-neutral-900"}`}>
    <span className="font-mono-tech">9:41</span>
    <div className="flex items-center gap-1">
      <span className="inline-block h-2 w-3 rounded-[2px] bg-current opacity-70" />
      <span className="inline-block h-2.5 w-1.5 rounded-[2px] bg-current" />
      <span className="inline-block h-2.5 w-5 rounded-[3px] border border-current" />
    </div>
  </div>
);

const Brand = () => (
  <div className="flex items-center gap-2">
    <div className="grid h-7 w-7 place-items-center rounded-lg bg-brand text-white text-[11px] font-bold">SOS</div>
    <span className="text-sm font-semibold tracking-tight">SOSConnect <span className="text-brand">AI</span></span>
  </div>
);

function HomeScreen() {
  const items = [
    { icon: Car, label: "Roadside", c: "bg-brand/10 text-brand" },
    { icon: KeyRound, label: "Lockout", c: "bg-ai/10 text-ai" },
    { icon: Droplets, label: "Plumbing", c: "bg-sky-500/10 text-sky-600" },
    { icon: ShieldCheck, label: "Locksmith", c: "bg-neutral-900/5 text-neutral-700" },
  ];
  return (
    <div className="h-full bg-[#FAFAFC] px-5">
      <StatusBar />
      <div className="flex items-center justify-between py-3"><Brand /><div className="h-8 w-8 rounded-full bg-neutral-200" /></div>
      <p className="text-[15px] font-semibold text-neutral-500">Good afternoon,</p>
      <h3 className="mb-4 text-2xl font-bold tracking-tight text-neutral-900">What do you need help with?</h3>
      <div className="mb-4 flex items-center gap-2 rounded-2xl bg-white p-3 shadow-sm ring-1 ring-black/5">
        <Sparkles className="h-4 w-4 text-ai" />
        <span className="text-sm text-neutral-400">Describe your emergency…</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {items.map((it) => (
          <div key={it.label} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-black/5">
            <div className={`mb-6 grid h-10 w-10 place-items-center rounded-xl ${it.c}`}><it.icon className="h-5 w-5" /></div>
            <p className="text-sm font-semibold text-neutral-800">{it.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatScreen() {
  return (
    <div className="flex h-full flex-col bg-white px-5">
      <StatusBar />
      <div className="flex items-center gap-2 py-3"><Brand /></div>
      <div className="flex-1 space-y-3 pt-2">
        <div className="ml-auto max-w-[80%] rounded-2xl rounded-br-md bg-brand px-4 py-3 text-[13px] font-medium text-white shadow-sm">
          My tire blew out and I'm stuck on the side of the road.
        </div>
        <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-neutral-100 px-4 py-3 text-[13px] text-neutral-800">
          Got it. I'll help you find nearby roadside assistance for a tire replacement or tow.
        </div>
      </div>
      <div className="mb-6 mt-2 flex items-center gap-2 rounded-full bg-neutral-100 px-4 py-2.5">
        <span className="flex-1 text-[13px] text-neutral-400">Message SOSConnect AI…</span>
        <div className="grid h-8 w-8 place-items-center rounded-full bg-brand text-white"><Send className="h-4 w-4" /></div>
      </div>
    </div>
  );
}

function AIScreen() {
  return (
    <div className="flex h-full flex-col justify-center bg-gradient-to-b from-white to-brand/5 px-6 text-center">
      <StatusBar />
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="relative mb-6">
          <div className="absolute inset-0 rounded-full bg-ai/30 blur-2xl" />
          <div className="relative grid h-20 w-20 place-items-center rounded-full bg-white shadow-lg ring-1 ring-ai/20">
            <Sparkles className="h-9 w-9 text-ai" />
          </div>
        </div>
        <p className="text-[13px] font-semibold uppercase tracking-widest text-ai">Analyzing</p>
        <h3 className="mt-2 text-lg font-bold text-neutral-900">Roadside — Tire replacement</h3>
        <p className="mt-1 px-4 text-[13px] text-neutral-500">Matching you with providers who carry your tire size nearby.</p>
      </div>
    </div>
  );
}

function ProvidersScreen() {
  const list = [
    { name: "RapidRoad Assist", rating: "4.9", eta: "8 min", d: "1.2 mi" },
    { name: "City Tire & Tow", rating: "4.8", eta: "12 min", d: "2.4 mi" },
    { name: "24/7 Roadside Pros", rating: "4.7", eta: "15 min", d: "3.1 mi" },
  ];
  return (
    <div className="h-full bg-[#FAFAFC] px-5">
      <StatusBar />
      <h3 className="py-3 text-lg font-bold text-neutral-900">Nearby help</h3>
      <div className="space-y-3">
        {list.map((p, i) => (
          <div key={p.name} className={`rounded-2xl bg-white p-4 shadow-sm ring-1 ${i === 0 ? "ring-brand" : "ring-black/5"}`}>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-neutral-900">{p.name}</p>
              <span className="flex items-center gap-1 text-xs font-semibold text-neutral-700"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{p.rating}</span>
            </div>
            <div className="mt-2 flex items-center gap-3 text-[11px] font-medium text-neutral-500">
              <span className="flex items-center gap-1 text-ai"><Clock className="h-3 w-3" />{p.eta}</span>
              <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{p.d}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function TrackingScreen() {
  return (
    <div className="relative h-full bg-[#EAF0F8]">
      <StatusBar />
      <svg viewBox="0 0 300 380" className="absolute inset-0 h-full w-full opacity-90">
        <rect width="300" height="380" fill="#E7EEF7" />
        {[60, 130, 200, 270].map((y) => <line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#D3DEEC" strokeWidth="1" />)}
        {[70, 150, 230].map((x) => <line key={x} x1={x} y1="0" x2={x} y2="380" stroke="#D3DEEC" strokeWidth="1" />)}
        <path d="M40 320 C 110 280, 120 180, 200 140 S 250 70, 250 70" fill="none" stroke="#0055FF" strokeWidth="4" strokeLinecap="round" strokeDasharray="360" strokeDashoffset="360" style={{ animation: "route-dash 2.4s ease forwards" }} />
        <circle cx="40" cy="320" r="7" fill="#0055FF" />
        <circle cx="250" cy="70" r="7" fill="#34C759" />
      </svg>
      <div className="absolute left-4 right-4 top-16 flex items-center gap-2 rounded-2xl bg-white/90 px-4 py-3 shadow-lg backdrop-blur">
        <div className="grid h-9 w-9 place-items-center rounded-full bg-ai/10 text-ai"><Navigation className="h-4 w-4" /></div>
        <div><p className="text-[11px] font-semibold uppercase tracking-wide text-ai">On the way</p><p className="text-sm font-bold text-neutral-900">ETA 12 minutes</p></div>
      </div>
      <div className="absolute bottom-5 left-4 right-4 rounded-2xl bg-white p-4 shadow-xl ring-1 ring-black/5">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-neutral-200" />
          <div className="flex-1"><p className="text-sm font-bold text-neutral-900">Marcus T.</p><p className="text-[11px] text-neutral-500">RapidRoad · White Ford F-150</p></div>
          <span className="flex items-center gap-1 text-xs font-semibold"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />4.9</span>
          <div className="grid h-9 w-9 place-items-center rounded-full bg-brand text-white"><Phone className="h-4 w-4" /></div>
        </div>
      </div>
    </div>
  );
}

function DoneScreen() {
  return (
    <div className="flex h-full flex-col items-center justify-center bg-gradient-to-b from-ai/10 to-white px-6 text-center">
      <StatusBar />
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="relative mb-5">
          <div className="absolute inset-0 rounded-full bg-ai/40 blur-2xl" />
          <div className="relative grid h-20 w-20 place-items-center rounded-full bg-ai text-white shadow-lg"><Check className="h-10 w-10" strokeWidth={3} /></div>
        </div>
        <h3 className="text-xl font-bold text-neutral-900">Problem solved</h3>
        <p className="mt-1 text-[13px] text-neutral-500">Service complete. Payment handled.</p>
        <div className="mt-5 flex gap-1">{[0,1,2,3,4].map((i) => <Star key={i} className="h-6 w-6 fill-amber-400 text-amber-400" />)}</div>
        <p className="mt-2 text-[12px] font-medium text-neutral-500">Rate your experience</p>
      </div>
    </div>
  );
}

const SCREENS = {
  home: HomeScreen, chat: ChatScreen, ai: AIScreen,
  providers: ProvidersScreen, tracking: TrackingScreen, done: DoneScreen,
};

export default function PhoneMockup({ screen = "home", className = "", animateKey }) {
  const { assets } = useAssets();
  const entry = assets?.[`screen_${screen}`];
  const shot = assetUrl(assets, `screen_${screen}`);
  const contain = entry?.fit === "contain";
  const Screen = SCREENS[screen] || HomeScreen;
  return (
    <div className={`phone-frame w-[290px] ${className}`} data-testid="phone-mockup">
      <div className="phone-screen aspect-[9/19.2]" style={shot && contain ? { background: entry.bg || "#0A0A0A" } : undefined}>
        <div className="absolute left-1/2 top-2 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-black" />
        <AnimatePresence mode="wait">
          <motion.div
            key={animateKey ?? screen}
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="h-full w-full"
          >
            {shot ? (
              <img src={shot} alt="App screen" className={`h-full w-full ${contain ? "object-contain" : "object-cover"}`} />
            ) : (
              <Screen />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
